<template>
    <div class="rtb-campaign-form">
        <div class="rtb-campaign-form__inner">
            <header class="poi-header">
                <h1 class="page-title">{{ layer?.id ? 'POI-слой' : 'Новый POI-слой' }}</h1>
                <a href="/rtb/poi-layers" class="btn btn--secondary">К списку</a>
            </header>

            <form class="form" @submit.prevent="save">
                <section class="rtb-form-section">
                    <label class="field">
                        <span>Название</span>
                        <input v-model="name" class="form-input" type="text" maxlength="255" :disabled="!canEdit" required />
                    </label>
                    <p v-if="errors.name" class="field__error">{{ errors.name }}</p>
                </section>

                <section class="rtb-form-section">
                    <h2 class="poi-section-title">Точки</h2>
                    <ul v-if="points.length" class="poi-points">
                        <li v-for="(point, index) in points" :key="`point-${index}`">
                            <span>{{ index + 1 }}. {{ point.lat.toFixed(5) }}, {{ point.lng.toFixed(5) }}</span>
                            <label class="field">
                                <span>Радиус, м</span>
                                <input
                                    v-model.number="point.radius_m"
                                    class="form-input"
                                    type="number"
                                    min="100"
                                    max="50000"
                                    step="1"
                                    :disabled="!canEdit"
                                />
                            </label>
                            <button type="button" class="btn btn--secondary btn--small" :disabled="!canEdit" @click="points.splice(index, 1)">
                                Удалить
                            </button>
                        </li>
                    </ul>
                </section>

                <section class="rtb-form-section">
                    <p v-if="framesLoading" class="field__hint">Загрузка карты…</p>
                    <p v-else-if="framesFailed" class="field__hint">Не удалось загрузить экраны.</p>
                    <GoogleMap
                        v-else
                        :frames="frames"
                        :selected-frame-gids="selectedGids"
                        sidebar-mode="screens"
                        :disable-price-check="true"
                        :hide-required-star="true"
                        :allow-multi-ssp="true"
                        :multi-filters="true"
                        :show-organization-filter="isAdmin || isAgency"
                        :read-only="!canEdit"
                        :can-manage-test-frames="isAdmin"
                        :show-week-requests="true"
                        :points="points"
                        :points-draggable="canEdit"
                        @update:selected-frame-gids="onSelectionChange"
                        @map-click="addPoint"
                        @point-move="movePoint"
                    />
                </section>

                <section v-if="canEdit" class="rtb-form-section">
                    <h2 class="poi-section-title">Кто видит слой</h2>
                    <p class="field__hint">Человек видит слой и может запустить кампанию. Точки и радиус он не меняет.</p>
                    <div class="rtb-form-section__row">
                        <label class="field">
                            <span>Email</span>
                            <input
                                v-model="userQuery"
                                class="form-input"
                                type="search"
                                :placeholder="canBrowseUsers ? 'Часть email' : 'Точный email'"
                            />
                        </label>
                        <label class="field">
                            <span>Роль</span>
                            <select v-model="roleFilter" class="select-input">
                                <option value="">Все роли</option>
                                <option v-for="option in ROLE_FILTER_OPTIONS" :key="option.code" :value="option.code">
                                    {{ option.label }}
                                </option>
                            </select>
                        </label>
                    </div>
                    <p v-if="needsExactEmail" class="field__hint">Укажите точный email.</p>
                    <p v-else-if="usersLoading && !visibleHits.length" class="field__hint">Ищем…</p>
                    <ul v-else-if="visibleHits.length" class="poi-share">
                        <li v-for="user in visibleHits" :key="user.id">
                            <span>{{ user.email }}</span>
                            <span class="poi-role">{{ roleLabel(user.role) }}</span>
                            <button type="button" @click="grant(user)">Добавить</button>
                        </li>
                    </ul>
                    <p v-else class="field__hint">Никого не нашлось.</p>
                    <p v-if="usersHasMore" class="field__hint">Показаны первые 50. Уточните email.</p>
                    <ul v-if="granted.length" class="poi-share">
                        <li v-for="user in granted" :key="`granted-${user.id}`">
                            <span>{{ user.email }}</span>
                            <span class="poi-role">{{ roleLabel(user.role) }}</span>
                            <span v-if="user.is_active === false" class="poi-inactive">Неактивен</span>
                            <button type="button" @click="revoke(user.id)">убрать</button>
                        </li>
                    </ul>
                    <p v-else class="field__hint">Доступ пока никому не выдан.</p>
                </section>

                <footer class="rtb-form-footer">
                    <p v-if="generalError" class="form-error" role="alert">{{ generalError }}</p>
                    <div class="rtb-form-footer__actions">
                        <button v-if="canEdit" class="btn btn--primary" type="submit" :disabled="saving">
                            {{ saving ? 'Сохранение…' : 'Сохранить' }}
                        </button>
                        <a v-if="layer?.id" :href="`/rtb/poi-layers/${layer.id}/export`" class="btn btn--secondary">Скачать Excel</a>
                        <a v-if="layer?.can_campaign" :href="layer.campaign_create_url" class="btn btn--secondary">Создать кампанию</a>
                        <a href="/rtb/poi-layers" class="btn btn--secondary">{{ canEdit ? 'Отмена' : 'Назад' }}</a>
                    </div>
                </footer>
            </form>
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import axios from 'axios';
import GoogleMap from '../../common/GoogleMap.vue';
import { confirmAction } from '../../../composables/useConfirm';
import { ROLE_FILTER_OPTIONS, roleLabel } from '../../../constants/roles';

const props = defineProps({
    userData: { type: Object, default: () => ({}) },
    framesUrl: { type: String, required: true },
    layer: { type: Object, default: null },
});

const name = ref(props.layer?.name || '');
const points = ref((props.layer?.points || []).map(point => ({
    lat: Number(point.lat),
    lng: Number(point.lng),
    radius_m: Number(point.radius_m) || 1000,
})));
const excludeGids = ref((props.layer?.exclude_gids || []).map(gid => String(gid)));
const includeGids = ref((props.layer?.include_gids || []).map(gid => String(gid)));
const granted = ref([...(props.layer?.users || [])]);
const frames = ref([]);
const framesLoading = ref(true);
const framesFailed = ref(false);
const selectedGids = ref((props.layer?.frames || []).map(row => String(row.gid)));
const userQuery = ref('');
const roleFilter = ref('');
const userHits = ref([]);
const usersHasMore = ref(false);
const usersLoading = ref(false);
const errors = ref({});
const generalError = ref('');
const saving = ref(false);
let userTimer = null;
let usersSeq = 0;
let previewTimer = null;
let previewSeq = 0;

const canEdit = computed(() => (props.layer ? props.layer.can_edit !== false : props.userData?.can_write !== false));
const isAdmin = computed(() => Boolean(props.userData?.is_admin));
const isAgency = computed(() => Boolean(props.userData?.is_agency));
const canBrowseUsers = computed(() => isAdmin.value || isAgency.value);
const needsExactEmail = computed(() => !canBrowseUsers.value && userQuery.value.trim() === '');
const visibleHits = computed(() => {
    const taken = new Set(granted.value.map(user => user.id));
    return userHits.value.filter(user => !taken.has(user.id));
});

const gidKey = (gid) => String(gid);

const exclude = (gid) => {
    const key = gidKey(gid);
    if (!excludeGids.value.includes(key)) excludeGids.value.push(key);
    includeGids.value = includeGids.value.filter(item => item !== key);
};

const addManual = (gid) => {
    const key = gidKey(gid);
    excludeGids.value = excludeGids.value.filter(item => item !== key);
    if (!includeGids.value.includes(key)) includeGids.value.push(key);
};

const onSelectionChange = (nextGids) => {
    if (!canEdit.value) return;
    const prev = new Set(selectedGids.value.map(gidKey));
    const next = new Set((nextGids || []).map(gidKey));
    let changed = false;
    prev.forEach((gid) => {
        if (!next.has(gid)) {
            exclude(gid);
            changed = true;
        }
    });
    next.forEach((gid) => {
        if (!prev.has(gid)) {
            addManual(gid);
            changed = true;
        }
    });
    if (changed) selectedGids.value = [...next];
};

const addPoint = ({ lat, lng }) => {
    if (!canEdit.value || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
    points.value.push({ lat, lng, radius_m: 1000 });
};

const movePoint = ({ index, lat, lng }) => {
    if (!points.value[index] || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
    points.value[index].lat = lat;
    points.value[index].lng = lng;
};

const grant = (user) => {
    if (granted.value.some(item => item.id === user.id)) return;
    granted.value.push({ ...user, is_active: true });
};

const revoke = (userId) => {
    granted.value = granted.value.filter(item => item.id !== userId);
};

const loadUsers = async () => {
    const seq = ++usersSeq;
    const query = userQuery.value.trim();
    if (!canEdit.value || (!canBrowseUsers.value && query === '')) {
        userHits.value = [];
        usersHasMore.value = false;
        usersLoading.value = false;
        return;
    }
    usersLoading.value = true;
    try {
        const params = { q: query };
        if (roleFilter.value) params.role = roleFilter.value;
        const { data } = await axios.get('/rtb/poi-layers/users', { params });
        if (seq !== usersSeq) return;
        userHits.value = data.users || [];
        usersHasMore.value = Boolean(data.has_more);
    } catch {
        if (seq !== usersSeq) return;
        userHits.value = [];
        usersHasMore.value = false;
    } finally {
        if (seq === usersSeq) usersLoading.value = false;
    }
};

watch([userQuery, roleFilter], () => {
    if (!canEdit.value) return;
    clearTimeout(userTimer);
    userTimer = setTimeout(loadUsers, 250);
}, { immediate: true });

const refreshPreview = async () => {
    const seq = ++previewSeq;
    try {
        const { data } = await axios.post('/rtb/poi-layers/preview', {
            layer_id: props.layer?.id || null,
            points: points.value,
            exclude_gids: excludeGids.value,
            include_gids: includeGids.value,
        });
        if (seq !== previewSeq) return;
        selectedGids.value = (data.frames || []).map(row => String(row.gid));
        if (generalError.value === 'Не удалось пересчитать экраны') generalError.value = '';
    } catch {
        if (seq !== previewSeq) return;
        generalError.value = 'Не удалось пересчитать экраны';
    }
};

watch([points, excludeGids, includeGids], () => {
    clearTimeout(previewTimer);
    previewTimer = setTimeout(refreshPreview, 300);
}, { deep: true, immediate: true });

const save = async () => {
    if (!canEdit.value || saving.value) return;
    if (props.layer?.has_campaigns) {
        const confirmed = await confirmAction({
            title: 'По этому слою уже есть кампании',
            message: 'Новые кампании возьмут новый расчёт. Уже запущенные останутся на своём снимке экранов.',
            confirmLabel: 'Сохранить слой',
        });
        if (!confirmed) return;
    }
    saving.value = true;
    generalError.value = '';
    errors.value = {};
    const payload = {
        name: name.value.trim(),
        points: points.value,
        exclude_gids: excludeGids.value,
        include_gids: includeGids.value,
        user_ids: granted.value.map(user => user.id),
    };
    try {
        const url = props.layer?.id ? `/rtb/poi-layers/${props.layer.id}` : '/rtb/poi-layers';
        const method = props.layer?.id ? 'put' : 'post';
        const { data } = await axios[method](url, payload);
        if (data.redirect) {
            window.location.assign(data.redirect);
            return;
        }
        window.location.reload();
    } catch (error) {
        const response = error?.response?.data;
        errors.value = response?.errors || {};
        generalError.value = response?.message || 'Не удалось сохранить слой';
    } finally {
        saving.value = false;
    }
};

onMounted(async () => {
    try {
        const { data } = await axios.get(props.framesUrl);
        frames.value = data.frames || [];
    } catch {
        framesFailed.value = true;
    } finally {
        framesLoading.value = false;
    }
});
</script>

<style lang="scss">
@use '../../../../scss/components/rtb-campaign-form';
</style>

<style scoped lang="scss">
.poi-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.poi-section-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
}

.poi-points,
.poi-share {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin: 0;
    padding: 0;
    list-style: none;
}

.poi-points li,
.poi-share li {
    display: flex;
    align-items: center;
    gap: 12px;
}

.poi-points li .field {
    flex: 0 0 160px;
}

.poi-share li span:first-child {
    flex: 1;
    min-width: 0;
}

.poi-role {
    color: #6b7280;
    font-size: 13px;
}

.poi-inactive {
    color: #b45309;
    font-size: 12px;
}

.poi-share button {
    background: none;
    border: 0;
    color: #1d4ed8;
    cursor: pointer;
}
</style>
